import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Link,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  AdminPanelSettings
} from '@mui/icons-material';
import { adminLogin, getUserProfileDetail } from '../../store/actions';
import logo from '../../assets/images/logo.jpg';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (emailError && value) {
      setEmailError('');
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    if (passwordError && value) {
      setPasswordError('');
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const validateForm = () => {
    let isValid = true;

    if (!email) {
      setEmailError('Email is required');
      isValid = false;
    } else if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      isValid = false;
    }

    if (!password) {
      setPasswordError('Password is required');
      isValid = false;
    } else if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }
    const payload = { email, password };

    dispatch(adminLogin(payload, (res) => {      
      if (res?.code === 200) {
        navigate('/admin/dashboard');
        const payload = { userId: res?.data?.userId ? res?.data?.userId  : res?.data?._id };
        dispatch(
          getUserProfileDetail(payload, (result) => {
          })
        );
      } else {
        setError(res?.message || 'Login failed. Please check your credentials.');
      }
    }));
  };

  const handleForgotPassword = () => {
    // Navigate to forgot password page or open modal
    navigate('/forgot-password');
  };

  return (
    <Card className="login-card">
      <CardContent className="login-card-content">
        {/* Header */}
        <Box className="login-header">
          <Box className="login-header-icon">
            <img src={logo} alt="Logo" className="admin-login-logo" />
          </Box>
          <Typography variant="h4" className="login-title">
            Admin Portal
          </Typography>
          <Typography variant="body1" className="login-subtitle">
            Sign in to access your dashboard
          </Typography>
        </Box>

        <Divider className="login-divider" />

        {/* Error Alert */}
        {error && (
          <Alert severity="error" className="login-error">
            {error}
          </Alert>
        )}

        {/* Login Form */}
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={handleEmailChange}
            error={!!emailError}
            helperText={emailError}
            margin="normal"
            required
            autoComplete="email"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email color="action" />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={handlePasswordChange}
            error={!!passwordError}
            helperText={passwordError}
            margin="normal"
            required
            autoComplete="current-password"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Forgot Password Link */}
          <Box className="login-forgot-wrapper">
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={handleForgotPassword}
              className="login-forgot-link"
            >
              Forgot Password?
            </Link>
          </Box>

          {/* Login Button */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            className="login-button"
          >
            Sign In
          </Button>

          {/* Additional Options */}
          <Box className="login-help">
            <Typography variant="body2">
              Need help? Contact{' '}
              <Link href="mailto:support@hermony.au" className="login-help-link">
                support@hermony.au
              </Link>
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

};

export default AdminLogin;